import { useMemo, useState } from "react";
import {
  computeGrossDurationHours,
  isOvernightShift,
  isValidBufferMinutes,
  isValidOneDecimalHours,
  isZeroLengthShift,
} from "../utils/shiftTimeUtils";

/**
 * Save-time + field-level validation for the Create/Edit Shift form.
 * Handles granular constraints, dependency metrics, and multi-field validation.
 */
export function useShiftValidation(formState, existingShifts = []) {
  const [fieldErrors, setFieldErrors] = useState({});

  const grossDurationHours = useMemo(
    () => computeGrossDurationHours(formState.shiftStartTime, formState.shiftEndTime),
    [formState.shiftStartTime, formState.shiftEndTime]
  );

  const isOvernight = useMemo(
    () => isOvernightShift(formState.shiftStartTime, formState.shiftEndTime),
    [formState.shiftStartTime, formState.shiftEndTime]
  );

  // Core validation runner that extracts logic without triggering React component state updates
  const calculateFieldError = (field, value, currentForm) => {
    let error = "";

    if (field === "shiftName") {
      if (!value || !value.trim()) {
        error = "Shift name is required.";
      } else if (
        existingShifts.some(
          (s) => (s.shiftName || "").toLowerCase() === value.trim().toLowerCase() && s.id !== currentForm.id
        )
      ) {
        error = "A shift with this name already exists.";
      }
    }

    if (field === "shiftStartTime" && !value) {
      error = "Shift start time is required.";
    }

    if (field === "shiftEndTime") {
      if (!value) {
        error = "Shift end time is required.";
      } else if (isZeroLengthShift(currentForm.shiftStartTime, value)) {
        error = "End time cannot equal start time — this is a zero-length shift.";
      }
    }

    const duration = computeGrossDurationHours(currentForm.shiftStartTime, currentForm.shiftEndTime);

    if (field === "strictWorkingHours") {
      if (!isValidOneDecimalHours(value)) {
        error = "Enter a value with up to one decimal place.";
      } else if (currentForm.halfDayWorkingHours && Number(value) > Number(currentForm.halfDayWorkingHours)) {
        error = "Strict Working Hours must be ≤ Half Day Working Hours.";
      } else if (duration !== null && Number(value) > duration) {
        error = "Cannot exceed the shift's gross duration.";
      }
    }

    if (field === "halfDayWorkingHours") {
      if (!isValidOneDecimalHours(value)) {
        error = "Enter a value with up to one decimal place.";
      } else if (currentForm.strictWorkingHours && Number(value) < Number(currentForm.strictWorkingHours)) {
        error = "Half Day Working Hours must be ≥ Strict Working Hours.";
      } else if (currentForm.fullDayWorkingHours && Number(value) > Number(currentForm.fullDayWorkingHours)) {
        error = "Half Day Working Hours must be ≤ Full Day Working Hours.";
      }
    }

    if (field === "fullDayWorkingHours") {
      if (!isValidOneDecimalHours(value)) {
        error = "Enter a value with up to one decimal place.";
      } else if (currentForm.halfDayWorkingHours && Number(value) < Number(currentForm.halfDayWorkingHours)) {
        error = "Full Day Working Hours must be ≥ Half Day Working Hours.";
      } else if (duration !== null && Number(value) > duration) {
        error = "Cannot exceed the shift's gross duration.";
      }
    }

    if (field === "lateArrivalBufferMinutes" && !isValidBufferMinutes(value)) {
      error = "Enter a whole number ≥ 0.";
    }

    if (field === "earlyExitBufferMinutes" && !isValidBufferMinutes(value)) {
      error = "Enter a whole number ≥ 0.";
    }

    if (currentForm.overtimeEnabled) {
      if (field === "minimumOvertimeHours") {
        if (!isValidOneDecimalHours(value)) {
          error = "Enter a value with up to one decimal place.";
        } else if (currentForm.halfDayOvertimeHours && Number(value) > Number(currentForm.halfDayOvertimeHours)) {
          error = "Minimum Overtime Hours must be ≤ Half Day Overtime Hours.";
        }
      }

      if (field === "halfDayOvertimeHours") {
        if (!isValidOneDecimalHours(value)) {
          error = "Enter a value with up to one decimal place.";
        } else if (currentForm.minimumOvertimeHours && Number(value) < Number(currentForm.minimumOvertimeHours)) {
          error = "Half Day Overtime Hours must be ≥ Minimum Overtime Hours.";
        } else if (currentForm.fullDayOvertimeHours && Number(value) > Number(currentForm.fullDayOvertimeHours)) {
          error = "Half Day Overtime Hours must be ≤ Full Day Overtime Hours.";
        }
      }

      if (field === "fullDayOvertimeHours") {
        if (!isValidOneDecimalHours(value)) {
          error = "Enter a value with up to one decimal place.";
        } else if (currentForm.halfDayOvertimeHours && Number(value) < Number(currentForm.halfDayOvertimeHours)) {
          error = "Full Day Overtime Hours must be ≥ Half Day Overtime Hours.";
        }
      }
    }

    return error;
  };

  // Field-level validator for immediate runtime checks
  const validateField = (field, value, currentForm = formState) => {
    const error = calculateFieldError(field, value, currentForm);

    setFieldErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next[field] = error;
      } else {
        delete next[field];
      }
      return next;
    });

    return error;
  };

  // Fixed global engine: Single atomic write to error state wrapper
  const runGlobalValidation = () => {
    const globalErrors = {};
    const fieldsToCheck = [
      "shiftName",
      "shiftStartTime",
      "shiftEndTime",
      "strictWorkingHours",
      "halfDayWorkingHours",
      "fullDayWorkingHours",
      "lateArrivalBufferMinutes",
      "earlyExitBufferMinutes",
    ];

    if (formState.overtimeEnabled) {
      fieldsToCheck.push("minimumOvertimeHours", "halfDayOvertimeHours", "fullDayOvertimeHours");
    }

    fieldsToCheck.forEach((field) => {
      const error = calculateFieldError(field, formState[field], formState);
      if (error) {
        globalErrors[field] = error;
      }
    });

    setFieldErrors(globalErrors);
    return globalErrors;
  };

  const isFormValid = useMemo(() => {
    if (!formState.shiftName?.trim() || !formState.shiftStartTime || !formState.shiftEndTime) return false;
    if (isZeroLengthShift(formState.shiftStartTime, formState.shiftEndTime)) return false;
    if (
      !isValidOneDecimalHours(formState.strictWorkingHours) ||
      !isValidOneDecimalHours(formState.halfDayWorkingHours) ||
      !isValidOneDecimalHours(formState.fullDayWorkingHours)
    ) {
      return false;
    }
    if (Number(formState.strictWorkingHours) > Number(formState.halfDayWorkingHours)) return false;
    if (Number(formState.halfDayWorkingHours) > Number(formState.fullDayWorkingHours)) return false;
    if (grossDurationHours !== null && Number(formState.fullDayWorkingHours) > grossDurationHours) return false;
    if (
      !isValidBufferMinutes(formState.lateArrivalBufferMinutes) ||
      !isValidBufferMinutes(formState.earlyExitBufferMinutes)
    ) {
      return false;
    }

    if (formState.overtimeEnabled) {
      if (
        !isValidOneDecimalHours(formState.minimumOvertimeHours) ||
        !isValidOneDecimalHours(formState.halfDayOvertimeHours) ||
        !isValidOneDecimalHours(formState.fullDayOvertimeHours)
      ) {
        return false;
      }
      if (Number(formState.minimumOvertimeHours) > Number(formState.halfDayOvertimeHours)) return false;
      if (Number(formState.halfDayOvertimeHours) > Number(formState.fullDayOvertimeHours)) return false;
    }

    return true;
  }, [formState, grossDurationHours]);

  return {
    fieldErrors,
    setFieldErrors,
    validateField,
    runGlobalValidation,
    isFormValid,
    grossDurationHours,
    isOvernight,
  };
}